<template>
  <div class="column full-height q-pl-lg">
    <div class="col-1 row q-my-sm" style="max-height: 40px">
      <div class="col-1 q-ml-md flex items-center">
        <span>シナリオ名</span>
      </div>
      <div class="col-4">
        <q-input bottom-slots v-model="campaignName" label="シナリオ名" dense>
          <template v-slot:append>
            <q-icon
              v-if="campaignName !== form.title"
              name="close"
              @click="campaignName = form.title"
              class="cursor-pointer"
              color="negative"
            />
            <q-icon
              v-if="campaignName !== form.title"
              name="done"
              @click="updateCampaignName"
              class="cursor-pointer"
              color="positive"
            />
          </template>
        </q-input>
      </div>
      <div class="col-2 q-ml-md flex items-center">
        <span>シナリオ共有ストーリー：</span>
        <span class="q-ml-md cursor-pointer hyper-link-chart" @click="onOpenUsageStoryModal">
          {{ numberWithCommas(form.stories_using_this_push) }}
        </span>
      </div>
    </div>
    <div class="col row">
      <div class="col" style="border-right: 1px solid #d0d2e0">
        <ScenariosMaker
          v-model="scenarios"
          :campaignId="form._id"
          :directionType="directionType"
          :isTestMode="isTestMode"
          :isReport="false"
          :campaign="form"
          :campaign-type="'push'"
          :selectedPushIndex="selectedPushIndex"
          :previousCampaign="previousCampaign"
          :isOpenStartSettings="openStartSettings"
          :isStoryFirstPush="isStoryFirstPush"
          :isCopy="isCopy"
          :story="story"
          @update:onChangeDirection="onChangeDirection"
          @update:onChangeScenarios="onChangeScenarios"
          @update:openStartSettings="(value) => (openStartSettings = value)"
          @update:campaignId="onChangeCampaignId"
          @updateStartSettings="onUpdateStartSettings"
          ref="scenarioEditor"
        />
      </div>
    </div>
  </div>
  <UsageStoryModal
    v-if="usageStoryModalVisible"
    :modalVisible="usageStoryModalVisible"
    :campaignId="form._id"
    :campaignType="'push'"
    @update:closeModal="onCloseUsageStoryModal"
  />
</template>

<script lang='ts'>
import { mixins, Options } from 'vue-class-component'
import PushCampaignMixin from '@/components/push-campaign/mixins/PushCampaignMixin.vue'
import BaseFormMixin from '@/components/common/mixins/BaseFormMixin.vue'
import ScenariosMaker from '@/components/story/scenarios/ScenariosMaker.vue'
import { maska } from 'maska'
import { Prop, Watch } from 'vue-property-decorator'
import { IInitialCampaign, IPushCampaign, IStory } from '@/utils/types'
import { ICard } from 'bot-flow-maker/src/types'
import cloneDeep from 'lodash/cloneDeep'
import { STARTING_POINT_TYPE, TRIGGER_TYPE, UNIT_TYPE, DELIVERY_TARGET_TYPE } from '@/utils/constants'
import { Formater } from '@/utils/formater'
import UsageStoryModal from '@/components/story/UsageStoryModal.vue'

@Options({
  components: { UsageStoryModal, ScenariosMaker },
  directives: { maska },
  emits: ['update:modelValue', 'update:onSave', 'update:onNextStep', 'update:openStartSettings'],
  async beforeRouteLeave() {
    if (!this) {
      return
    }
    this.isCancel = false
    return await this.checkDraftValue()
  },
})
export default class Push extends mixins(PushCampaignMixin, BaseFormMixin) {
  @Prop({})
  modelValue!: IStory

  @Prop()
  selectedPushIndex!: number

  @Prop({})
  isTestMode!: boolean

  @Prop()
  isOpenStartSettings!: boolean

  @Prop()
  isStoryFirstPush!: boolean

  @Prop({})
  isCopy!: boolean

  previousCampaign: IPushCampaign | IInitialCampaign = {}
  scenarios: ICard[] = []
  directionType = '0'
  campaignName: string | undefined = ''
  usageStoryModalVisible = false
  form: IPushCampaign = {
    _id: '',
    title: '',
    app_id: '',
    delivered: 0,
    is_active: false,
    is_test_mode: false,
    audience_size: 0,
    prod_detail: {
      starting_point: STARTING_POINT_TYPE.SCENARIO_START,
      direction_type: 1,
      audience: {
        card_or: [],
        card_not: [],
        audience_or: [],
        audience_not: [],
        unreached_users: [],
      },

      frequency: {
        active: false,
        time_per_user: 1,
      },
      schedule: {
        schedule_type: 'multi_time',
        schedule_by_type: 'by_delay_date',
        delay_day: 0,
        day_of_week: 0,
        day_of_month: 1,
        date: '',
        time: '',
      },
      scenarios: [],
    },
    test_detail: {
      starting_point: STARTING_POINT_TYPE.SCENARIO_START,
      direction_type: 1,
      audience: {
        card_or: [],
        card_not: [],
        audience_or: [],
        audience_not: [],
        unreached_users: [],
      },

      frequency: {
        active: false,
        time_per_user: 1,
      },
      schedule: {
        schedule_type: 'multi_time',
        schedule_by_type: 'by_delay_date',
        delay_day: 0,
        day_of_week: 0,
        day_of_month: 1,
        date: '',
        time: '',
      },
      scenarios: [],
    },
  }

  get story() {
    return this.modelValue
  }

  set story(value: IStory) {
    this.$emit('update:modelValue', value)
  }

  get openStartSettings() {
    return this.isOpenStartSettings
  }

  set openStartSettings(value: boolean) {
    this.$emit('update:openStartSettings', value)
  }

  @Watch('isTestMode', { immediate: true })
  @Watch('selectedPushIndex', { immediate: true })
  async onChangeTestMode() {
    if (this.selectedPushIndex === 0 && this.story.trigger_type === TRIGGER_TYPE.AUDIENCE) {
      if (this.story.push) {
        this.form = cloneDeep(this.story.push)
      }
    } else {
      if (this.story.pushes) {
        this.form = cloneDeep(this.story.pushes[this.selectedPushIndex])
        if (
          this.story.trigger_type === TRIGGER_TYPE.POPUP_DELIVER ||
          this.story.trigger_type === TRIGGER_TYPE.QR_CODE ||
          this.story.trigger_type === TRIGGER_TYPE.DEFAULT
        ) {
          if (this.selectedPushIndex === 0) {
            this.previousCampaign = this.story.initial ?? {}
          } else {
            this.previousCampaign = cloneDeep(this.story.pushes[this.selectedPushIndex - 1])
          }
        } else if (this.story.trigger_type === TRIGGER_TYPE.AUDIENCE) {
          this.previousCampaign = cloneDeep(this.story.pushes[this.selectedPushIndex - 1])
        }
      }
    }

    if (this.isTestMode) {
      if (this.form.test_detail) {
        this.scenarios = this.form.test_detail.scenarios ?? []
        this.directionType = this.form.test_detail.direction_type?.toString() ?? '0'
        if (this.story.trigger_type === TRIGGER_TYPE.AUDIENCE) {
          if (!this.form.test_detail.starting_point) {
            this.form.test_detail.starting_point = STARTING_POINT_TYPE.SCENARIO_START
          }
        }
        if (!this.form.test_detail.direction_type) {
          this.form.test_detail.direction_type = 1
        }
      }
    } else {
      if (this.form.prod_detail) {
        this.scenarios = this.form.prod_detail.scenarios ?? []
        this.directionType = this.form.prod_detail.direction_type?.toString() ?? '0'
      }
    }
    this.campaignName = this.form.title
  }

  onChangeDirection(value) {
    this.directionType = value
    if (this.isTestMode) {
      if (this.form.test_detail) {
        this.form.test_detail.direction_type = Number.parseInt(value)
      }
    } else {
      if (this.form.prod_detail) {
        this.form.prod_detail.direction_type = Number.parseInt(value)
      }
    }
  }

  onChangeScenarios(value) {
    this.scenarios = value
    if (this.isTestMode) {
      if (this.form.test_detail) {
        this.form.test_detail.scenarios = value
      }
    } else {
      if (this.form.prod_detail) {
        this.form.prod_detail.scenarios = value
      }
    }

    if (this.story.pushes) {
      this.story.pushes[this.selectedPushIndex] = cloneDeep(this.form)
      if (this.selectedPushIndex === 0 && this.story.trigger_type === TRIGGER_TYPE.AUDIENCE) {
        this.story.push = this.form
      }
    }
  }

  validate(isSave = false) {
    this.onSubmit(isSave)
  }

  async onSubmit(isSave = false) {
    if (this.isStoryFirstPush) {
      // this.form.is_active = this.story.is_active
      // this.form.is_test_mode = this.story.is_test_mode
      this.story.push = cloneDeep(this.form)
    }

    if (this.story.pushes) {
      // this.form.is_active = this.story.is_active
      // this.form.is_test_mode = this.story.is_test_mode
      this.story.pushes[this.selectedPushIndex] = cloneDeep(this.form)
    }

    if (isSave) {
      this.$emit('update:onSave')
    } else {
      this.$emit('update:onNextStep')
    }
  }

  onUpdateStartSettings(value) {
    if (this.story.pushes) {
      this.story.pushes[this.selectedPushIndex] = cloneDeep(this.form)
    }
  }

  updateCampaignName() {
    if (this.story.pushes) {
      this.form.title = this.campaignName
      this.story.pushes[this.selectedPushIndex].title = this.campaignName
    }
  }

  onChangeCampaignId(selectedCampaign: IPushCampaign, isNewId: boolean) {
    if (isNewId) {
      this.form._id = ''
    } else {
      this.form = selectedCampaign as IPushCampaign
    }
    if (this.form.prod_detail) {
      if (!this.form.prod_detail.schedule) {
        this.form.prod_detail.schedule = {
          schedule_type: 'multi_time',
          schedule_by_type: 'by_delay_date',
          delay_day: 0,
          day_of_week: 0,
          day_of_month: 1,
          date: '',
          time: '',
        }
      }
      if (!this.form.prod_detail.audience) {
        this.form.prod_detail.audience = {
          card_or: [],
          card_not: [],
          audience_or: [],
          audience_not: [],
          unreached_users: [],
        }
      }
      if (!this.form.prod_detail.frequency) {
        this.form.prod_detail.frequency = {
          active: false,
          time_per_user: 1,
        }
      }
      if (!this.form.prod_detail.starting_point) {
        this.form.prod_detail.starting_point = STARTING_POINT_TYPE.SCENARIO_START
      }
      if (!this.form.prod_detail.direction_type) {
        this.form.prod_detail.direction_type = 1
      }
      if (!this.form.prod_detail.unit_type) {
        this.form.prod_detail.unit_type = UNIT_TYPE.MESSAGE_QA_UNIT
      }
      if (!this.form.prod_detail.delivery_target_type) {
        this.form.prod_detail.delivery_target_type = DELIVERY_TARGET_TYPE.RESPONDING_USERS
      }
    }

    if (this.form.test_detail) {
      if (!this.form.test_detail.schedule) {
        this.form.test_detail.schedule = this.form.prod_detail!.schedule
      }
      if (!this.form.test_detail.audience) {
        this.form.test_detail.audience = this.form.prod_detail!.audience
      }
      if (!this.form.test_detail.frequency) {
        this.form.test_detail.frequency = this.form.prod_detail!.frequency
      }
      if (!this.form.test_detail.starting_point) {
        this.form.test_detail.starting_point = STARTING_POINT_TYPE.SCENARIO_START
      }
      if (!this.form.test_detail.direction_type) {
        this.form.test_detail.direction_type = 1
      }
      if (!this.form.test_detail.unit_type) {
        this.form.test_detail.unit_type = this.form.prod_detail!.unit_type
      }
      if (!this.form.test_detail.delivery_target_type) {
        this.form.test_detail.delivery_target_type = this.form.prod_detail!.delivery_target_type
      }
    }
  }

  onOpenUsageStoryModal() {
    this.usageStoryModalVisible = true
  }

  onCloseUsageStoryModal() {
    this.usageStoryModalVisible = false
  }

  numberWithCommas(num) {
    return Formater.numberFormat(num)
  }

  created() {
    this.onChangeTestMode()
    this.parentForm = this.form as any
    this.initForm = cloneDeep(this.parentForm) as any
    this.campaignName = this.form.title
  }
}
</script>

<style scoped lang='scss'>
.q-field--with-bottom {
  padding-bottom: 0;
}
span.hyper-link-chart {
  color: #4956e3;
  text-decoration: underline;
}
</style>
